/** Copy-paste integration snippets with the service's real (public) identifiers filled in. */
import type { Service } from './types';

export interface SnippetValues {
  serviceId: string;
  keyId: string;
  prymeKeyId: string;
  prymePublicKey: string;
  apiBase: string;
  apiAudience: string;
  /** Where people sign in (`/authorize`); may differ from the API base. */
  issuer: string;
  redirectUri: string;
  claims: string[];
  environment: 'sandbox' | 'production';
}

export function snippetValues(s: Service): SnippetValues {
  const b = s.keys.find((k) => k.direction === 'B' && k.status === 'active');
  const a = s.keys.find((k) => k.direction === 'A' && k.status === 'active');
  return {
    serviceId: s.id,
    keyId: b?.id ?? '<signing key id>',
    prymeKeyId: a?.id ?? '<Pryme verification key id>',
    prymePublicKey: a?.public_key ?? '<Pryme verification key>',
    apiBase: s.apiBase,
    apiAudience: s.apiAudience,
    issuer: s.issuer,
    redirectUri: s.redirectUris[0] ?? (s.environment === 'sandbox' ? 'http://localhost:3000/auth/pryme/callback' : 'https://your.domain/auth/pryme/callback'),
    claims: s.claimConfig.requested?.length ? s.claimConfig.requested : ['human.verified'],
    environment: s.environment,
  };
}

const q = (s: string) => `'${s.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
const arr = (xs: string[]) => `[${xs.map(q).join(', ')}]`;

export function envExample(v: SnippetValues) {
  const prefix = v.environment === 'production' ? 'sk_live_' : 'sk_test_';
  return `# .env — backend only. PRYME_SECRET_KEY is the ${prefix}… value from the one-time credentials panel.
PRYME_SERVICE_ID=${v.serviceId}
PRYME_SECRET_KEY=<${prefix}… — shown once>
COOKIE_SECRET=<at least 32 random bytes>`;
}

function configBlock(v: SnippetValues, env = 'process.env') {
  return `const pryme = createPryme({
  serviceId: ${env}.PRYME_SERVICE_ID!,   // ${v.serviceId}
  // Backend only. Signs your requests and verifies Pryme's responses; it also carries the API settings.
  secretKey: ${env}.PRYME_SECRET_KEY!,
  redirectUri: ${q(v.redirectUri)},
  claims: ${arr(v.claims)},
  environment: ${q(v.environment)},
});`;
}

export function expressSnippet(v: SnippetValues) {
  return `// npm i @pryme/sdk express express-session
import express from 'express';
import session from 'express-session';
import { createPryme } from '@pryme/sdk';
import { prymeExpress, toIncoming } from '@pryme/sdk/express';

${configBlock(v)}
const auth = prymeExpress(pryme, { cookieSecret: process.env.COOKIE_SECRET! });

const app = express();
app.use(session({ secret: process.env.COOKIE_SECRET!, resave: false, saveUninitialized: false }));

// 1. "Continue with Pryme" → Authorization Code + PKCE (S256), state & nonce in a sealed cookie
app.get('/login', auth.login({ claims: ${arr(v.claims)} }));

// 2. Callback: validates state/iss, exchanges the code with a Service-B-signed /token call
app.get('/auth/pryme/callback', auth.callback({
  onLogin: async (req, res, { identity, session: prymeSession }) => {
    // identity.subject is a pairwise ID: stable for YOUR service only.
    // Link it to your own user record. For an existing account, require that the
    // user is already signed in (or re-authenticates) before linking.
    (req as any).session.pryme = prymeSession;
    res.redirect(303, '/');
  },
}));

// 3. Any request — browser session or a signed agent request (raw body needed for digest check)
app.post('/checkout', express.raw({ type: '*/*' }), async (req, res) => {
  const id = await pryme.verify(toIncoming(req), { session: (req as any).session?.pryme });
  if (!id.isGenuine()) return id.reject(res);
  if (!id.isOver(18)) return id.rejectWithMessage(res, 'Not old enough');

  const { orderId, amount } = JSON.parse(req.body.toString() || '{}');
  // 4. Delegated (agent) requests: online, transaction-specific mandate check
  if (id.isDelegated()) {
    // Forwards the agent's signed intent: Pryme allows only exactly the operation the agent signed.
    const d = await id.authorizeDetailed('commerce.purchase', { amount, currency: 'DKK', orderId });
    if (d.outcome !== 'allow') return id.reject(res); // 202 approval_required {approval: {id}} or 403 {error: {code}}
    // A retry after a lost response is allowed again with the same d.authorizationId (d.idempotent):
    // Pryme reserves once, but only you can make the action run once. Record it under orderId (or
    // d.authorizationId) before acting; for a repeat, return the stored result instead.
    // ... perform the action once, then settle or release the reservation:
    await pryme.settle(id, d.authorizationId!, 'settled');
  }
  res.sendStatus(204);
});

app.listen(3000);`;
}

export function honoSnippet(v: SnippetValues) {
  return `// Cloudflare Workers / Hono / any fetch runtime. npm i @pryme/sdk hono
import { Hono } from 'hono';
import { getCookie, setCookie, deleteCookie } from 'hono/cookie';
import { createPryme } from '@pryme/sdk';
import { sealTransaction, openTransaction } from '@pryme/sdk/express'; // runtime-agnostic helpers

type Env = Record<'PRYME_SERVICE_ID' | 'PRYME_SECRET_KEY' | 'COOKIE_SECRET', string>;
const app = new Hono<{ Bindings: Env }>();

const prymeFor = (env: Env) => {
  ${configBlock(v, 'env').split('\n').join('\n  ')}
  return pryme;
};

app.get('/login', async (c) => {
  const { url, transaction } = prymeFor(c.env).beginLogin();
  setCookie(c, 'pryme_tx', await sealTransaction(c.env.COOKIE_SECRET, transaction), { httpOnly: true, secure: true, sameSite: 'Lax', maxAge: 600 });
  return c.redirect(url, 303);
});

app.get('/auth/pryme/callback', async (c) => {
  const tx = await openTransaction(c.env.COOKIE_SECRET, getCookie(c, 'pryme_tx'));
  deleteCookie(c, 'pryme_tx');
  const { identity, session } = await prymeFor(c.env).completeLogin(new URL(c.req.url).searchParams, tx);
  // Store \`session\` ({ subject, state, binding }) server-side, keyed by YOUR session cookie.
  return c.json({ subject: identity.subject, over18: identity.isOver(18) });
});

app.post('/checkout', async (c) => {
  const pryme = prymeFor(c.env);
  const id = await pryme.verify(c.req.raw, { session: /* your stored Pryme session */ null });
  if (!id.isGenuine()) return id.reject() as Response;
  if (id.isDelegated() && !(await id.authorize('commerce.purchase', { amount: 2499, currency: 'DKK', orderId: 'ord_123' }))) {
    return id.reject() as Response;
  }
  // A retry is allowed again with the same authorizationId: perform each orderId once, recorded in your own store.
  return c.body(null, 204);
});

export default app;`;
}

export function browserSnippet(v: SnippetValues) {
  return `<!-- Browser: the button only navigates to YOUR backend, which starts the PKCE flow.
     Never put PRYME_SECRET_KEY (or any Pryme credential) in browser JavaScript. -->
<a class="pryme-continue" href="/login">
  <span aria-hidden="true">◆</span> Continue with Pryme
</a>
<style>
  .pryme-continue { display:inline-flex; gap:.5rem; align-items:center; padding:.65rem 1.1rem;
    border-radius:10px; background:#1b1446; color:#fff; font:600 15px system-ui; text-decoration:none; }
  .pryme-continue:focus-visible { outline:3px solid #8f7bff; outline-offset:2px; }
</style>

<!-- What your /login route redirects to (built by pryme.beginLogin()):
${v.issuer}/authorize?response_type=code&client_id=${v.serviceId}
  &redirect_uri=${encodeURIComponent(v.redirectUri)}&scope=openid
  &claims=${encodeURIComponent(v.claims.join(' '))}&state=…&nonce=…
  &code_challenge=…&code_challenge_method=S256 -->`;
}

export function agentSnippet(v: SnippetValues) {
  return `// Agent runtime (NOT your service backend). The agent has its OWN key: it pairs once with the
// person (pairAgent: they approve a short code with their passkey) and keeps the resulting agent
// secret key (sk_test_… / sk_live_…) in its own secret store. No human key is ever given to it.
import { createAgent } from '@pryme/sdk/agent';

const agent = createAgent({ secretKey: process.env.PRYME_AGENT_SECRET_KEY! });

// Human-signed mandate proofs granted to this agent, for this recipient service
const proof = (await agent.mandates()).find((m) => m.recipient === ${q(v.serviceId)});
if (!proof) throw new Error('no mandate for ${v.serviceId}');

// Exactly the operation the service will check (identity.authorize). The agent signs it: only then
// can the service use the person's permission for it, once. Without it, the service can only ask them.
const intent = { action: 'commerce.purchase', amount: 2499, currency: 'DKK', orderId: 'ord_123' }; // minor units

// Signed request bound to method + path + body digest + audience (= recipient serviceId) + nonce
let { res } = await agent.request('https://your.domain/checkout', {
  recipient: ${q(v.serviceId)},
  proof,
  intent,
  body: { orderId: 'ord_123', amount: 2499, currency: 'DKK' },
});

if (res.status === 202) {
  const { approval } = await res.json();
  // The human approves this exact operation in the Pryme portal, then the agent retries:
  await agent.waitForApproval(approval.id);
  ({ res } = await agent.request('https://your.domain/checkout', {
    recipient: ${q(v.serviceId)}, proof, intent, approvalId: approval.id,
    body: { orderId: 'ord_123', amount: 2499, currency: 'DKK' },
  }));
}`;
}

export function activationSnippet(v: SnippetValues, challenge: string) {
  return `// activate.mjs — run on your backend with your .env loaded (node --env-file=.env activate.mjs)
import { createPryme } from '@pryme/sdk';

const pryme = createPryme({
  serviceId: process.env.PRYME_SERVICE_ID,   // ${v.serviceId}
  secretKey: process.env.PRYME_SECRET_KEY,
});

// Signs POST /v1/services/activate with your secret key and checks Pryme's response signature.
console.log(await pryme.activate(${q(challenge)}));`;
}

export function signatureBaseExample(v: SnippetValues) {
  return `pryme-request-v1
principal: service:${v.serviceId}
kid: ${v.keyId}
method: POST
audience: ${v.apiAudience}
target: /v1/verify
content-digest: sha-256=<base64url(SHA-256(exact body bytes))>
timestamp: 2026-10-31T12:00:00Z
nonce: <≥16 chars, random, single use>`;
}
